// A tab that was open across a deploy, reaching a page it has to fetch (#457).
//
// `index.html` is `no-store` and assets are hashed and immutable (ZENITH.md
// §18, ADR-0003), so a *fresh* navigation always lands on the current bundle.
// A tab that loaded before the deploy did not: it is still executing the old
// entry chunk, and that chunk names its lazy chunks by the hash they had when
// it was built. Those files are not in the new image. The first lazy route the
// person then visits — Topology, the console, the neighbourhood, carry-over —
// fetches a 404, `import()` rejects, and `Boundary` tells them the page could
// not be drawn, which reads as Zenith being broken at the moment they are most
// likely to be watching a site: during a deploy.
//
// §18 anticipates the half of this that is about caching, and `spa.go`
// implements it correctly. This is the other half: a shell that was current
// when it loaded and went stale while the tab stayed open. No cache is
// involved, so no cache header addresses it.
//
// Vite's preload helper already dispatches the event this needs, and rethrows
// only if nothing cancels it, so the whole fix is a listener:
//
//   function i(e){ let t=new Event(`vite:preloadError`,{cancelable:!0});
//                  t.payload=e; window.dispatchEvent(t);
//                  if(!t.defaultPrevented) throw e }
//   return r.then(t => { …; return e().catch(i) })
//
// `e()` is the dynamic import itself, so a missing chunk reaches this and not
// only a failed preload link.
//
// **The passive half of this already existed and is kept**: `Version` polls
// `/version` and offers a Reload when the server's build differs from the
// bundle's. That notice cannot win this race — its timer is fifteen minutes and
// its other trigger is the tab regaining focus, while the navigation happens
// the moment the person clicks. The two agree about what a stale tab is; this
// is the one that runs first.

/** Where the attempt is remembered. Session-scoped: a new tab is a new chance,
 * and nothing here is worth keeping once the browser is closed. */
export const STALE_BUNDLE_KEY = "zenith:stale-bundle-reload";

interface Options {
  /** The build this bundle was compiled from. */
  build?: string;
  /** How to start again. Injected so a test does not navigate jsdom. */
  reload?: () => void;
}

/**
 * Reload once when a chunk this bundle names has gone from the server.
 *
 * Returns a function that stops listening.
 *
 * **The guard is keyed to the build, not a boolean**, and the difference is not
 * cosmetic. What has to be prevented is reloading into the *same* bundle
 * forever, which is what happens when a build genuinely lacks a chunk its own
 * entry references — reloading cannot fix that, and a loop is worse than a
 * message. A boolean prevents that case and also breaks a correct one: a tab
 * that survived two deploys would be refused its second reload and shown the
 * error box, having done nothing wrong. Recording which build already tried
 * separates them, and `Version` already keeps the same identity for the same
 * reason.
 *
 * **When there is nowhere to remember the attempt, nothing is suppressed.**
 * Blocked site data makes the guard unavailable, and an unguarded reload is the
 * loop above. So the error surfaces and `Boundary` says so, which is this
 * function declining to act rather than failing.
 */
export function installStaleBundleReload({
  build = __ZENITH_COMMIT__,
  reload = () => window.location.reload(),
}: Options = {}): () => void {
  const onPreloadError = (event: Event) => {
    let attempted: string | null;
    try {
      attempted = sessionStorage.getItem(STALE_BUNDLE_KEY);
    } catch {
      return; // No guard available: let it be reported rather than risk a loop.
    }
    // This build already reloaded and the chunk is still gone. Reloading again
    // would land on the same bundle and ask the same question.
    if (attempted === build) return;
    try {
      sessionStorage.setItem(STALE_BUNDLE_KEY, build);
    } catch {
      return; // As above: the attempt could not be recorded, so it is not made.
    }
    // Cancel before reloading, so the helper does not rethrow into a boundary
    // that would paint an error for the instant before the page goes away.
    event.preventDefault();
    reload();
  };
  window.addEventListener("vite:preloadError", onPreloadError);
  return () => window.removeEventListener("vite:preloadError", onPreloadError);
}
